import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Dropdown,
  Option,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Checkbox,
  Badge,
  Field,
  tokens,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, ColumnTripleRegular } from '@fluentui/react-icons';
import { format } from 'date-fns';
import { getPreparerByYearRoster } from '../../services/api';
import type { TaxReturn } from '../../types';
import { exportToExcel, dateStampedFilename } from '../../utils/excelExport';
import { isSmsCapablePhone } from '../../utils/smsCapable';
import { SearchableMultiSelect } from '../listFilters/SearchableMultiSelect';
import { DataTable, type Column } from '../DataTable';

interface PreparerOption {
  id: string;
  name: string;
}

interface PreparerRosterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preparerOptions: PreparerOption[];
  years: number[];
}

// Wave 3 (Christian's walk, 2026-11-02, item B): "make it a real list" -- a column-choosable
// DataTable rather than a hand-rolled <table> of three fixed columns, multi-select preparers via
// the SAME SearchableMultiSelect combobox item A built (C86), and export carrying exactly the
// columns on screen, in their order (FB-11's exportToExcel helper, zero new export code).
//
// "SMS-capable" and "in-house"/"client language" are not simple passthroughs of existing data --
// see smsCapable.ts and api/Functions/PreparerByYearFunctions.cs's own doc comments for exactly
// what each one actually measures and why (there is no Twilio Lookup integration anywhere in this
// fleet, and no persisted "client language preference" field -- both are honestly-labelled
// approximations of the nearest REAL data this backend already has, not invented facts).

type RosterColumnKey =
  | 'clientName' | 'clientEmail' | 'clientPhone' | 'smsCapable'
  | 'returnType' | 'scheduledDate' | 'status' | 'isInHouse' | 'clientLanguage';

const DEFAULT_VISIBLE: RosterColumnKey[] = [
  'clientName', 'clientEmail', 'clientPhone', 'returnType', 'scheduledDate', 'status',
];

const STORAGE_KEY = 'preparerRosterVisibleColumns';

function loadVisibleColumns(): RosterColumnKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((k) => typeof k === 'string')) {
        return parsed as RosterColumnKey[];
      }
    }
  } catch {
    // ignore -- fall back to the default set
  }
  return DEFAULT_VISIBLE;
}

/** Sanitize a preparer-name fragment for use in a filename (no path separators, quotes, etc.). */
function filenameSafe(s: string): string {
  return s.replace(/[^a-zA-Z0-9-]+/g, '_').replace(/^_+|_+$/g, '');
}

export default function PreparerRosterDialog({ open, onOpenChange, preparerOptions, years }: PreparerRosterDialogProps) {
  const { t } = useTranslation();
  const [preparerIds, setPreparerIds] = useState<string[]>(() => (preparerOptions[0] ? [preparerOptions[0].id] : []));
  const [year, setYear] = useState<number | undefined>(years[0]);
  const [visibleColumns, setVisibleColumns] = useState<RosterColumnKey[]>(loadVisibleColumns);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch {
      // ignore storage errors -- the chooser still works for this session
    }
  }, [visibleColumns]);

  const { data: rosterByPreparer, isLoading } = useQuery<TaxReturn[][]>({
    queryKey: ['preparerByYearRoster', preparerIds, year],
    queryFn: () => Promise.all(preparerIds.map((id) => getPreparerByYearRoster(id, year as number))),
    enabled: open && preparerIds.length > 0 && !!year,
  });

  // Multiple preparers can legitimately share a client+year row only in the sense of two separate
  // TaxReturn records (e.g. linked returns) -- dedupe by id defensively, never by client, so a
  // genuinely distinct return never silently disappears.
  const roster = useMemo(() => {
    const byId = new Map<string, TaxReturn>();
    for (const list of rosterByPreparer ?? []) {
      for (const r of list) byId.set(r.id, r);
    }
    return Array.from(byId.values());
  }, [rosterByPreparer]);

  const selectedPreparerNames = useMemo(
    () => preparerIds
      .map((id) => preparerOptions.find((p) => p.id === id)?.name)
      .filter((n): n is string => !!n),
    [preparerIds, preparerOptions]
  );

  function toggleColumn(key: RosterColumnKey) {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev; // never hide the last column
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }

  const ALL_COLUMN_KEYS: RosterColumnKey[] = [
    'clientName', 'clientEmail', 'clientPhone', 'smsCapable',
    'returnType', 'scheduledDate', 'status', 'isInHouse', 'clientLanguage',
  ];

  const columns: Column<TaxReturn>[] = useMemo(() => [
    { key: 'clientName', header: t('outreachPage.table.columns.client'), sortable: true, getValue: (r) => r.clientName },
    { key: 'clientEmail', header: t('outreachPage.rosterDialog.columns.email'), sortable: true, getValue: (r) => r.clientEmail ?? '' },
    { key: 'clientPhone', header: t('outreachPage.rosterDialog.columns.phone'), sortable: true, getValue: (r) => r.clientPhone ?? '' },
    {
      key: 'smsCapable',
      header: t('outreachPage.rosterDialog.columns.smsCapable'),
      sortable: true,
      getValue: (r) => isSmsCapablePhone(r.clientPhone),
      render: (r) => (isSmsCapablePhone(r.clientPhone) ? t('outreachPage.table.yes') : t('outreachPage.table.no')),
    },
    {
      key: 'returnType',
      header: t('outreachPage.table.columns.type'),
      sortable: true,
      getValue: (r) => r.returnType,
      render: (r) => (r.returnType === 'business' ? t('outreachPage.filters.business') : t('outreachPage.filters.individual')),
    },
    {
      key: 'scheduledDate',
      header: t('outreachPage.filters.scheduledDate'),
      sortable: true,
      getValue: (r) => r.scheduledDate,
      render: (r) => format(new Date(r.scheduledDate), 'MMM d, yyyy'),
    },
    { key: 'status', header: t('outreachPage.table.columns.status'), sortable: true, getValue: (r) => r.status },
    {
      key: 'isInHouse',
      header: t('outreachPage.rosterDialog.columns.inHouse'),
      sortable: true,
      getValue: (r) => r.isInHouse ?? false,
      render: (r) => (r.isInHouse ? t('outreachPage.table.yes') : t('outreachPage.table.no')),
    },
    {
      key: 'clientLanguage',
      header: t('outreachPage.rosterDialog.columns.clientLanguage'),
      sortable: true,
      getValue: (r) => r.clientLanguage ?? '',
      render: (r) => (r.clientLanguage ? r.clientLanguage.toUpperCase() : '—'),
    },
  ], [t]);

  // A SEPARATE plain-value mapping for export, rather than reusing Column.render (a ReactNode) --
  // every value here is already a plain string, matching exactly what's rendered on screen.
  const exportValue = useMemo<Record<RosterColumnKey, (r: TaxReturn) => string>>(() => ({
    clientName: (r) => r.clientName,
    clientEmail: (r) => r.clientEmail ?? '',
    clientPhone: (r) => r.clientPhone ?? '',
    smsCapable: (r) => (isSmsCapablePhone(r.clientPhone) ? t('outreachPage.table.yes') : t('outreachPage.table.no')),
    returnType: (r) => (r.returnType === 'business' ? t('outreachPage.filters.business') : t('outreachPage.filters.individual')),
    scheduledDate: (r) => format(new Date(r.scheduledDate), 'yyyy-MM-dd'),
    status: (r) => r.status,
    isInHouse: (r) => (r.isInHouse ? t('outreachPage.table.yes') : t('outreachPage.table.no')),
    clientLanguage: (r) => (r.clientLanguage ? r.clientLanguage.toUpperCase() : ''),
  }), [t]);

  const handleExport = async () => {
    if (roster.length === 0 || !year) return;
    const exportColumns = visibleColumns
      .map((key) => {
        const col = columns.find((c) => c.key === key);
        if (!col) return null;
        return { header: col.header, getValue: exportValue[key] };
      })
      .filter((c): c is { header: string; getValue: (r: TaxReturn) => string } => c !== null);

    const preparerLabel = selectedPreparerNames.length > 2
      ? `${selectedPreparerNames.slice(0, 2).map(filenameSafe).join('-')}-plus${selectedPreparerNames.length - 2}`
      : selectedPreparerNames.map(filenameSafe).join('-');

    await exportToExcel(dateStampedFilename(`preparer-roster-${preparerLabel}-TY${year}`), exportColumns, roster);
  };

  const preparerSelectOptions = useMemo(
    () => preparerOptions.map((p) => ({ value: p.id, label: p.name })),
    [preparerOptions]
  );

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '920px' }}>
        <DialogBody>
          <DialogTitle>{t('outreachPage.rosterDialog.title')}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '260px', flex: '1 1 260px' }}>
                {/* Multi-select of preparers, via the SAME searchable combobox item A built (C86)
                    -- Christian's own wording was "via the same combobox". */}
                <SearchableMultiSelect
                  label={t('outreachPage.rosterDialog.preparersLabel')}
                  options={preparerSelectOptions}
                  selected={preparerIds}
                  onChange={setPreparerIds}
                  searchPlaceholder={t('outreachPage.rosterDialog.preparerPlaceholder')}
                  labels={{
                    selectAll: t('common.selectAll'),
                    clear: t('common.clear'),
                    noMatches: t('common.noMatches'),
                    valueSuffix: t('common.value'),
                    searchSuffix: t('common.search'),
                  }}
                />
              </div>
              <div style={{ minWidth: '160px' }}>
                <Field label={t('outreachPage.rosterDialog.yearPlaceholder')}>
                  <Dropdown
                    placeholder={t('outreachPage.rosterDialog.yearPlaceholder')}
                    value={year ? `${t('outreachPage.rosterDialog.yearPlaceholder')} ${year}` : ''}
                    selectedOptions={year ? [String(year)] : []}
                    onOptionSelect={(_, data) => setYear(data.optionValue ? Number(data.optionValue) : undefined)}
                    style={{ minWidth: '160px' }}
                  >
                    {years.map((y) => (
                      <Option key={y} value={String(y)} text={`${t('outreachPage.rosterDialog.yearPlaceholder')} ${y}`}>
                        {`${t('outreachPage.rosterDialog.yearPlaceholder')} ${y}`}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Badge appearance="tint" style={{ fontSize: tokens.fontSizeBase200 }}>
                {t('outreachPage.rosterDialog.resultCount', { count: roster.length })}
              </Badge>
              <Popover>
                <PopoverTrigger disableButtonEnhancement>
                  <Button appearance="subtle" size="small" icon={<ColumnTripleRegular />}>
                    {t('outreachPage.rosterDialog.columnsButton')} ({visibleColumns.length}/{ALL_COLUMN_KEYS.length})
                  </Button>
                </PopoverTrigger>
                <PopoverSurface style={{ padding: '12px', minWidth: '220px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: tokens.fontSizeBase300 }}>
                    {t('outreachPage.rosterDialog.columnsPopoverTitle')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {ALL_COLUMN_KEYS.map((key) => (
                      <Checkbox
                        key={key}
                        checked={visibleColumns.includes(key)}
                        onChange={() => toggleColumn(key)}
                        label={columns.find((c) => c.key === key)?.header ?? key}
                      />
                    ))}
                  </div>
                </PopoverSurface>
              </Popover>
            </div>

            <DataTable
              data={roster}
              columns={columns}
              visibleColumns={visibleColumns}
              rowKey="id"
              loading={isLoading}
              emptyMessage={t('outreachPage.rosterDialog.empty')}
              paginated
              pageSize={25}
            />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              onClick={handleExport}
              disabled={roster.length === 0}
            >
              {t('outreachPage.rosterDialog.export')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
